import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { graphql, navigate } from 'gatsby'
import Seo from '~components/Seo'
import DataViewer from '~utils/DataViewer'
import PageHeader from '~components/PageHeader'
import { encodeQueryParams, NumberParam, StringParam, ArrayParam } from 'use-query-params'
import styled from '@emotion/styled'
import { Global, css } from '@emotion/react'
import ChecklistSlide from '~components/ChecklistSlide'
import { ChevronRightSmall } from '~components/Svg'
import Button from '~components/Button'
import { mobile, tablet } from '~styles/global'
import { Prev, Next } from '~components/Svg'
import { stringify } from 'query-string'
import { useSiteState } from '~context/siteContext'
import { v4 as uuidv4 } from 'uuid'
import axios from 'axios'

const Checklist = ({ data }) => {

	const { page } = data

	const [progress, setProgress] = useState(0)
	const [userData, setUserData] = useState({})
	const [maxProgress, setMaxProgress] = useState(0)
	const [complete, setComplete] = useState(false)
	const [siteState, setSiteState] = useSiteState()
	const [id, setId] = useState()
	const [timeOnChecklist, setTimeOnChecklist] = useState(0)
	const [timeInSection, setTimeInSection] = useState(0)

	// add timers
	useEffect(() => {
		const increaseTimers = () => {
			setTimeOnChecklist(prev => prev + 100)
			setTimeInSection(prev => prev + 100)
		}
		const timer = window.setInterval(increaseTimers, 100)
		return () => clearInterval(timer)
	}, [])

	const sanityPayload = {
		id: id,
		checklist: page.slug.current,
		answers: userData.answers,
	}

	const patchToSanity = data => {
		axios.post('/api/checklist-entry-submit', data)
			.then(function (response) {
				// console.log(response)
			})
			.catch(function (error) {
				// console.log(error)
			})
	}

	// create a unique id for this checklist that we'll save to sanity
	useEffect(() => {
		setId(uuidv4())
	}, [])

	// each time an answer changes, let's patch the checklist in sanity
	useEffect(() => {
		if(userData.answers){
			patchToSanity(sanityPayload)
		}
	}, [userData.answers])

	let questionsOnly = []
	page.sections.forEach(section => {
		questionsOnly = [...questionsOnly, ...(section.questions || [])]
	})

	const answersToNumbers = answers => answers?.map(answer => {
		const valueTable = {
			never: 0,
			sometimes: 1,
			often: 2,
			constantly: 3
		}
		return valueTable[answer.value] || 0
	})

	const calculateScore = answers => {
		const numbers = answersToNumbers(answers)

		// Check for red flags
		const redFlagAnswers = answers?.filter(answer => answer.question?.redFlag) || []
		const hasIndicatedRedFlag = redFlagAnswers?.filter(answer => answer.value !== 'never')?.length > 0
		
		if(hasIndicatedRedFlag){
			// return the max score
			return questionsOnly?.length ? questionsOnly.length * 3 : null
		} else {
			// add up the numbers
			const sum = numbers?.reduce((partialSum, a) => partialSum + a, 0)
			return sum
		}
	}

	const calculateResponse = score => {
		if(score === 0){
			return 'd'
		} else if (score < 6) {
			return 'c'
		} else if (score < 60) {
			return 'b'
		} else if (score >= 60) {
			return 'a'
		}
	}

	useEffect(() => {
		if(progress > maxProgress){
			setMaxProgress(progress)
		}
	}, [progress])


	// const userDataToQueryString = (d, r) => {
	// 	const { email, gender, answers } = d

	// 	const encodedQuery = encodeQueryParams(
	// 		{ 
	// 			r: StringParam,
	// 			a: ArrayParam,
	// 			c: StringParam,
	// 			e: StringParam,
	// 		}, 
	// 		{ 
	// 			r: r,
	// 			a: answersToNumbers(answers),
	// 			c: page.slug.current,
	// 			e: email,
	// 			g: gender,
	// 		}
	// 	)
	// 	return stringify(encodedQuery)
	// }

	// fires once, when the survey is completed 
	useEffect(() => {
		if(complete){
			// remove the window warning
			// window.onbeforeunload = null

			const score = calculateScore(userData.answers)
			const response = calculateResponse(score)
			// const encodedData = userDataToQueryString(userData, response)
			const { email, gender, answers, location } = userData
			const { title, sections } = page

			const checklistData = {
				checklist: page.slug.current,
				email, 
				answers,
				response,
				title, 
				sections,
			}

			setSiteState({...siteState, checklistData})

			patchToSanity({
				...sanityPayload, 
				gender, 
				location,
				complete: true,
				requestedEmail: Boolean(email)
			})

			const url = `/checklist-results/${page.slug.current}/${response}`
			navigate(url)
			
			
		}
		
		// Add a window warning
		// window.onbeforeunload = function() {
		// 	return true
		// }

	}, [complete])

	let sectionSlides = page.sections.map((section, n) => [
		{
			type: 'sectionIntro', 
			section: section.title,
			subtitle: section.subtitle, 
			sections: page.sections,
			background: section?.background?.value
		},
		...section.questions.map((question, i) => (
			{
				type: 'question', 
				section: section.title, 
				question: question,
				index: i,
				sections: page.sections,
				userData: userData,
				background: section?.background?.value
			}
		))
	])

	let slides = [
		...sectionSlides.flat(),
		// {type: 'details', section: 'complete', background: '#236D6D'},
	]

	slides = slides.map((slide, slideIndex) => ({...slide, slideIndex}))

	const nextSlide = trackingData => {
		
		let nextProgress = progress + 1
		if(nextProgress === slides.length){
			setComplete(true)
		} else {
			setProgress(progress + 1)

			// if we're changing sections, reset the section timer
			let nextSlide = slides[progress + 1]
			let currentSlide = slides[progress]
			if(nextSlide.section !== currentSlide.section){
				setTimeInSection(0)
			}
			window.scrollTo({
				top: 0,
				left: 0,
				behavior: 'smooth'
			})
		}

		if(trackingData){
			let d = {
				...trackingData,
				timeSincesectionStart: timeInSection,
				timeSinceStart: timeOnChecklist,
			}
			// console.log(d)
			window.dataLayer.push(d)
			// console.log(JSON.stringify(trackingData))
		}
	}

	const prevSlide = answer => {
		setProgress(progress - 1)
		window.scrollTo({
			top: 0,
			left: 0,
			behavior: 'smooth'
		})
	}

	const goToSlide = number => {
		if(number <= maxProgress){
			setProgress(number)
		}
	}

	const handleEmail = (email, trackingData) => {
		setUserData({...userData, email})
		nextSlide(trackingData)
	}

	const handleAnswer = answer => {
		if(answer?.value && answer?.question){
			let index = questionsOnly?.findIndex(question => question === answer.question)
			let section = page.sections?.find(section => section.title === answer.section)
			let sectionIndex = page.sections?.findIndex(section => section.title === answer.section)
			let questionIndexInSection = section?.questions?.findIndex(question => question === answer.question)

			// send trackingData
			const trackingData = {
				event: 'custom.form.answer.select',
				action: 'answer select',
				section: {
					name: answer.section,
					number: sectionIndex + 2,
					total: page.sections?.length + 2,
				},
				questions: {
					number: questionIndexInSection + 1,
					numberTotal: index + 1,
					name: answer?.question?.text,
					answer: answer?.value,
					totalsection: section?.questions?.length,
					total: questionsOnly?.length
				},
				checklist: page?.slug?.current
			}
			// console.log(trackingData)
			window.dataLayer.push(trackingData)

			// Add data to store
			if(userData?.answers && userData?.answers[index]){
				let tempData = userData
				tempData.answers[index] = {...answer, index}
			} else {
				setUserData({
					...userData, 
					answers: [
						...(userData.answers || []),
						{...answer, index}
					]
				})
			}
		}
	}

	const handleGender = (gender, trackingData) => {
		setUserData({...userData, gender})
		nextSlide(trackingData)
	} 

	const handleLocation = (location, trackingData) => {
		setUserData({...userData, location})
		nextSlide(trackingData)
	} 

	return(
		<>
			<Seo 
				title={page?.title}
				metaTitle={page?.seo?.metaTitle}
				description={page?.seo?.metaDescription}
				image={page?.seo?.socialImage}
			/>
			<Global styles={css`
				body{
					/* overflow: hidden; */
				}
			`}/>
			<Screen slide={slides[progress]}>
				<SectionTitle className='b2' slide={slides[progress]}>{slides[progress].type === 'question' ? slides[progress].section : ' '}</SectionTitle>
				<StyledChecklistSlide 
					slide={slides[progress]} 
					checklist={page} 
					next={nextSlide}
					key={progress}
					handleEmail={handleEmail}
					handleAnswer={handleAnswer}
					handleLocation={handleLocation}
					handleGender={handleGender}
					slides={slides}
				/>
				{progress > 0 &&
					<Tracker slide={slides[progress]}>
						<NavButton onClick={prevSlide} disabled={progress === 0}>
							<Prev/>
							<span>
								Prev
							</span>
						</NavButton>
						<Bar>
							<BarInner progress={(progress + 1) / slides.length * 100} />
						</Bar>
						<NavButton  disabled={progress >= maxProgress} hidden={progress >= (slides.length - 1)} onClick={nextSlide}>
							<span>
								Next
							</span>
							<Next/>
						</NavButton>
					</Tracker>
				}
			</Screen>
			<DataViewer data={data} name="data" />
		</>
	)
}

const SectionTitle = styled.p`
	margin-top: var(--s);
	min-height: 1.3em;
	color: ${props => props.slide?.background === '#236D6D' ? 'var(--white)' : 'inherit'};
`

export const Screen = styled.div`
	display: flex;
	flex-direction: column;
	/* position: fixed; */
	top: 150px;
	bottom: 0;
	left: 0;
	right: 0;
	transition: background-color 0.5s;
	background: ${props => props.slide?.background ?? '#D7D2CE'};
	z-index: 50;
	justify-content: center;
	align-items: center;
	overflow-y: scroll;
	padding: 0 var(--s);
`

const StyledChecklistSlide = styled(ChecklistSlide)`
	margin: var(--s) 0 var(--m) ;
	${mobile}{
		margin: var(--s) 0;
	}
`

const Nav = styled.nav`
	margin-top: var(--s);
	color: ${props => props.slide?.background === '#236D6D' ? 'var(--white)' : 'inherit'};
	${mobile}{
		margin-top: var(--xs);
	}
`

const Chevron = styled(ChevronRightSmall)`
	color: var(--bteal);
	width: 6px;
	padding: 0 var(--s);
	${mobile}{
		opacity: 0;
		padding: 0 var(--xs);
	}
`

const SectionButton = styled.button`
	font-weight: ${props => props.active ? 'bold' : 'normal'};
	opacity: ${props => props.disabled ? '0.5' : '1'};
	pointer-events: ${props => props.disabled ? 'none' : 'all'};
	${mobile}{
		span{
			display: none;
		}
	}
	
`

const MobNumber = styled.div`
	display: none;
	background: ${props => props.active ? 'var(--dteal)' : 'rgba(0, 0, 0, 0.15)'};
	color: ${props => props.active ? 'var(--white)' : 'inherit'};
	width: 20px;
	height: 20px;
	border-radius: 99px;
	${mobile}{
		display: flex;
		align-items: center;
		justify-content: center;
	}
`

const Tracker = styled.div`
	display: flex;
	width: 100%;
	align-items: center;
	margin-bottom: var(--l);
	color: ${props => props.slide?.background === '#236D6D' ? 'var(--white)' : 'inherit'};
	.button {
		color: ${props => props.slide?.background === '#236D6D' ? 'var(--white)' : 'inherit'};
	}
`

const Bar = styled.div`
	height: var(--xs);
	width: 100%;
	background: var(--white);
	border-radius: 99px;
	overflow: hidden;
	position: relative;
	${mobile}{
		margin: 0 var(--s);
	}
`

const BarInner = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	bottom: 0;
	background: var(--bteal);
	transition: width 0.5s;
	width: ${props => props.progress + '%'};
`

const NavButton = styled(Button)`
opacity: ${props => props.disabled ? '0.5' : '1'};
pointer-events: ${props => props.disabled ? 'none' : 'all'};
visibility: ${props => props.hidden ? 'hidden' : 'visible'};
> div {
	display: flex;
	align-items: center;
	svg{
		display: block;
		margin: 0 var(--xs);
		${mobile}{
			margin: 0;
		}
	}
}
${mobile}{
	min-width: 0;
	span{
		display: none;
	}
}
	
`

Checklist.propTypes = {
	data: PropTypes.object,
}

export const query = graphql`
  query ChecklistQuery($slug: String) {
    page: sanityDynamicChecklist(slug: {current: {eq: $slug}}) {
      title
			_type
			slug {
				current
			}
			sections {
				title
				subtitle
				questions {
					text
					subText
					redFlag
					answers
				}
				background {
					value
				}
			}
			requestGender
    }
  }
`

export default Checklist